import React from "react";
import { Link } from "react-router-dom";
import { Button } from "antd";

import Footer from "../components/Footer";
import Header from "../components/Header";
import "./GoogleReviewLinkGenerator.css";
import "./Pricing.css";

type PricingPlan = {
  id: string;
  name: string;
  description: string;
  price: number;
  memberPrice: number;
  period: string;
  features: string[];
  cta: string;
  ctaLink: string;
  featured?: boolean;
  badge?: string;
};

const PLANS: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    description: "Start with a review video link and QR code for your business.",
    price: 0,
    memberPrice: 0,
    period: "month",
    features: [
      "1 review video link profile",
      "QR code download",
      "Basic link page",
      "Email support",
    ],
    cta: "Get started free",
    ctaLink: "/google-review-link-generator",
  },
  {
    id: "pro",
    name: "Pro",
    description: "For businesses ready to grow reviews and customer engagement.",
    price: 29,
    memberPrice: 19,
    period: "month",
    featured: true,
    badge: "Most popular",
    features: [
      "Up to 5 link profiles",
      "Custom branding & colors",
      "Click analytics dashboard",
      "Priority email support",
      "Multiple QR codes",
    ],
    cta: "Start Pro trial",
    ctaLink: "/register",
  },
  {
    id: "business",
    name: "Business",
    description: "For teams managing multiple locations and campaigns.",
    price: 79,
    memberPrice: 59,
    period: "month",
    features: [
      "Unlimited link profiles",
      "Multi-store management",
      "Advanced analytics export",
      "Dedicated onboarding",
      "Team member access",
    ],
    cta: "Contact sales",
    ctaLink: "/register",
  },
];

function formatPrice(amount: number): string {
  return amount === 0 ? "Free" : `$${amount}`;
}

function getSavings(price: number, memberPrice: number): number {
  return Math.max(price - memberPrice, 0);
}

const PricingPlanCard: React.FC<{ plan: PricingPlan }> = ({ plan }) => {
  const savings = getSavings(plan.price, plan.memberPrice);
  const showMemberPricing = plan.price > 0 && plan.memberPrice < plan.price;

  return (
    <article
      className={`pricing-plan${plan.featured ? " pricing-plan--featured" : ""}`}
    >
      {plan.badge && <span className="pricing-plan-badge">{plan.badge}</span>}
      <h2 className="pricing-plan-name">{plan.name}</h2>
      <p className="pricing-plan-desc">{plan.description}</p>

      <div className="pricing-price-block">
        <div className="pricing-price-row">
          <span className="pricing-price">{formatPrice(plan.price)}</span>
          {plan.price > 0 && (
            <span className="pricing-price-period">/ {plan.period}</span>
          )}
        </div>

        {showMemberPricing && (
          <div className="pricing-member-row">
            <span className="pricing-member-label">Member price</span>
            <span className="pricing-member-price">
              {formatPrice(plan.memberPrice)}
            </span>
            <span className="pricing-original-price">
              {formatPrice(plan.price)}
            </span>
            {savings > 0 && (
              <span className="pricing-savings">Save ${savings}/mo</span>
            )}
          </div>
        )}
      </div>

      <ul className="pricing-features">
        {plan.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>

      <Link to={plan.ctaLink} className="pricing-plan-cta">
        <Button
          type="primary"
          size="large"
          block
          className="grlg-btn-primary"
        >
          {plan.cta}
        </Button>
      </Link>
    </article>
  );
};

const Pricing: React.FC = () => {
  return (
    <div
      className="pricing-page flex min-h-screen flex-col"
      style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      }}
    >
      <Header />
      <main className="pricing-main">
        <div className="pricing-hero">
          <p className="pricing-eyebrow">Plans & pricing</p>
          <h1 className="pricing-title">
            <span className="pricing-title-gradient">
              Choose the right plan for your business
            </span>
          </h1>
          <p className="pricing-subtitle">
            Compare standard and member pricing. Vioo members unlock lower rates
            on every paid plan.
          </p>
        </div>

        <div className="pricing-member-banner">
          <span className="pricing-member-badge">Member pricing</span>
          <p className="pricing-member-text">
            Already a Vioo member? Sign in to unlock member rates on Pro and
            Business plans.{" "}
            <Link to="/login" className="pricing-member-link">
              Sign in
            </Link>
          </p>
        </div>

        <div className="pricing-grid">
          {PLANS.map((plan) => (
            <PricingPlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        <section className="pricing-note-section">
          <h2 className="pricing-note-title">What counts as a member?</h2>
          <ol className="pricing-note-list">
            <li>
              Free accounts created through the review video generator are
              members with access to member-only pricing.
            </li>
            <li>
              Member prices apply while your subscription is active and your
              account is in good standing.
            </li>
            <li>
              Need a custom plan for franchises or agencies? Contact us and we
              will tailor pricing for your team.
            </li>
          </ol>
        </section>

        <p className="pricing-powered-by">
          Powered by{" "}
          <Link to="/" className="pricing-powered-by-link">
            Vioo.vip
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
